import { tenantRoute } from "../../utils/tenantRoute";
import { isOAuthPopupWindow } from "../../shared/accOAuth";

export interface ProviderReturnParams {
    code: string | null;
    state: string | null;
    error: string | null;
}
const CALLBACKS = ["/integrations"];
const MAX_AGE_MS = 10 * 60_000;
const captured = new Map<string, ProviderReturnParams>();
const key = (path: string) => `primepoint:oauth-return:${path}`;

/** Capture before React/analytics. Bounded tab storage survives reauthentication;
 * no credential or provider code is ever placed in a continuation URL. */
export function captureProviderReturnParams(onlyPath?: string): void {
    const path = tenantRoute().pathname;
    if (!CALLBACKS.includes(path) || (onlyPath && onlyPath !== path)) return;
    // The staff wizard popup is a distinct origin-checked postMessage relay.
    if (path === "/integrations" && isOAuthPopupWindow() && window.opener) return;
    const params = new URLSearchParams(window.location.search);
    if (params.has("code") || params.has("error")) {
        const value = {
            code: params.get("code"),
            state: params.get("state"),
            error: params.get("error"),
        };
        captured.set(path, value);
        try {
            sessionStorage.setItem(
                key(path),
                JSON.stringify({ value, expires: Date.now() + MAX_AGE_MS }),
            );
        } catch {
            /* Memory still works. */
        }
        params.delete("code");
        params.delete("state");
        params.delete("error");
        params.delete("error_description");
        const search = params.toString();
        window.history.replaceState(
            window.history.state,
            "",
            `${window.location.pathname}${search ? `?${search}` : ""}${window.location.hash}`,
        );
    } else if (!captured.has(path)) {
        try {
            const raw = sessionStorage.getItem(key(path));
            if (!raw) return;
            const stored = JSON.parse(raw);
            if (
                stored.expires > Date.now() &&
                stored.expires <= Date.now() + MAX_AGE_MS &&
                stored.value &&
                ["code", "state", "error"].every(
                    (k) => stored.value[k] === null || typeof stored.value[k] === "string",
                )
            )
                captured.set(path, stored.value);
            else sessionStorage.removeItem(key(path));
        } catch {
            /* Malformed or unavailable storage cannot restore a callback. */
        }
    }
}
export function peekProviderReturn(): { path: string; params: ProviderReturnParams } | null {
    const path = tenantRoute().pathname;
    const params = captured.get(path);
    return params ? { path, params } : null;
}
/** Read without consuming: transient failure or reauthentication must retain it. */
export function peekProviderReturnParams(path: string): ProviderReturnParams | null {
    return captured.get(path) ?? null;
}

/** Compatibility classification only, never authorization. The legacy callback
 * still verifies the signature and initiating actor on the server. */
export function isLegacyProviderReturnState(state: string): boolean {
    try {
        const claims = JSON.parse(atob(state.split(".")[0].replace(/-/g, "+").replace(/_/g, "/")));
        return (
            typeof claims.org === "string" &&
            typeof claims.member === "string" &&
            typeof claims.ts === "number" &&
            !["company_id", "project_id", "url_route_id"].some((k) => k in claims)
        );
    } catch {
        return false;
    }
}

export function takeProviderReturn(path: string): ProviderReturnParams | null {
    const value = captured.get(path) ?? null;
    captured.delete(path);
    try {
        sessionStorage.removeItem(key(path));
    } catch {
        /* Unavailable storage. */
    }
    return value;
}
